'use client';

import React from 'react';
import { Field } from '@/components/motor-insurance/enterprise-navy';
import { CheckboxDropdown } from '@/components/motor-insurance/CheckboxDropdown';
import {
  PAN_INDIA,
  stateLabel,
  withRto,
  withStates,
  type CategoryOption,
  type RateScope,
  type StateOption,
} from '@/lib/motor-insurance/rate-scope';

/** Choose an existing Product Category or type a new one: it is added to the list when the rate is saved. */
export function ProductCategoryField({
  value,
  onChange,
  categories,
  listId = 'product-category-options',
}: {
  value: string;
  onChange: (value: string) => void;
  categories: CategoryOption[];
  /** Must be unique on the page when several of these are shown. */
  listId?: string;
}) {
  return (
    <>
      <Field label="Product Category">
        <input
          list={listId}
          autoComplete="off"
          maxLength={50}
          placeholder="Select or type a new one"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
      <datalist id={listId}>
        {categories.map((c) => (
          <option key={c.code} value={c.label} />
        ))}
      </datalist>
    </>
  );
}

/**
 * State (any number can be ticked; none = Pan India) next to the RTO box. Each ticked state has its own
 * RTO box, because RTO numbers belong to one state.
 */
export function StateRtoFields({
  scope,
  onChange,
  states,
}: {
  scope: RateScope;
  onChange: (scope: RateScope) => void;
  states: StateOption[];
}) {
  const { stateCodes } = scope;
  const onlyState = stateCodes[0]; // the single ticked state, when exactly one is ticked
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 6, alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <Field label="State">
          <CheckboxDropdown
            options={states.map((st) => ({ value: st.code, label: st.name }))}
            selected={stateCodes}
            onChange={(next) => onChange(withStates(scope, next))}
            emptyLabel={PAN_INDIA}
            itemNoun="states"
            searchPlaceholder="Search states"
          />
        </Field>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {stateCodes.length <= 1 ? (
          <Field label="RTO">
            <input
              autoComplete="off"
              disabled={stateCodes.length === 0}
              placeholder={stateCodes.length === 0 ? 'Choose a state first' : 'e.g. 01, 04, 07'}
              value={onlyState !== undefined ? (scope.rtoByState[onlyState] ?? '') : ''}
              onChange={(e) => onlyState !== undefined && onChange(withRto(scope, onlyState, e.target.value))}
            />
          </Field>
        ) : (
          stateCodes.map((code) => (
            <Field key={code} label={`RTO – ${stateLabel(code, states)}`}>
              <input
                autoComplete="off"
                placeholder="e.g. 01, 04, 07"
                value={scope.rtoByState[code] ?? ''}
                onChange={(e) => onChange(withRto(scope, code, e.target.value))}
              />
            </Field>
          ))
        )}
      </div>
    </div>
  );
}

export function RateScopeNote() {
  return (
    <p style={{ fontSize: 11.5, color: 'var(--text-dim, #5B6B7A)', margin: '0 0 12px' }}>
      State: tick one or more; each ticked state is saved as its own rate, with its own RTO numbers
      (comma-separated; leave empty for all of that state&apos;s RTOs). A vehicle&apos;s state and RTO are read
      from its registration number, and a vehicle whose number cannot be read gets the rate that applies in
      every state. Product Category is only a label.
    </p>
  );
}
